import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogoWord } from "./Logo";
import {
  SquaresFour, Buildings, Target, Scan, Bug, FileText, ClipboardText, Gear,
  SignOut, List, ShieldCheck,
} from "@phosphor-icons/react";
import { Button } from "./ui/button";
import InactivityLock from "./InactivityLock";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/workspaces", label: "Workspaces", icon: Buildings },
  { to: "/targets", label: "Targets", icon: Target },
  { to: "/scans", label: "Scans", icon: Scan },
  { to: "/findings", label: "Findings", icon: Bug },
  { to: "/reports", label: "Reports", icon: FileText },
  { to: "/audit", label: "Audit", icon: ClipboardText },
  { to: "/settings", label: "Settings", icon: Gear },
];

const ROLE_LABEL = { admin: "Administrator", pentester: "Pentester", client: "Client" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-[#020817]">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 border-r border-[#1e293b] bg-[#0b1220] flex flex-col transition-transform duration-200 md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-5 py-5 border-b border-[#1e293b]">
          <LogoWord size={34} />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase()}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? "bg-blue-500/10 text-blue-400 border border-blue-500/30"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white border border-transparent"
                }`
              }
            >
              <n.icon size={19} weight="bold" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-[#1e293b]">
          <div className="flex items-center gap-1.5 px-2 pb-3 text-xs text-slate-500">
            <ShieldCheck size={15} weight="fill" className="text-blue-500" />
            Data resident in Australia East
          </div>
          <div className="rounded-md bg-slate-800/60 px-3 py-2.5 mb-2">
            <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
            <div className="overline text-blue-400">{ROLE_LABEL[user?.role]}</div>
          </div>
          <Button
            data-testid="logout-button"
            variant="ghost"
            onClick={doLogout}
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <SignOut size={18} weight="bold" className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/60 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 md:ml-64 min-w-0">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-[#1e293b] bg-[#020817]/90 backdrop-blur px-5 py-3 md:hidden">
          <button data-testid="menu-toggle" onClick={() => setOpen(true)} className="text-slate-300">
            <List size={24} />
          </button>
          <LogoWord size={28} />
        </header>
        <main className="px-5 md:px-8 py-6 md:py-8 max-w-[1400px]">{children}</main>
      </div>
      <InactivityLock />
    </div>
  );
}
